import React, { useState } from 'react';
import { mockData } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import * as LucideIcons from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        toast.success("Request Received!", {
          description: "Our team will contact you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Submission Failed", {
        description: error.response?.data?.detail || "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  // Simulated partner logos
  const partners = [
    'Tesla Energy', 'SolarEdge', 'AWS', 'Microsoft Azure',
    'Google Cloud', 'NVIDIA', 'Intel', 'AMD'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
                <LucideIcons.Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-foreground">
                {mockData.company.name}
              </span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#platform" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Platform</a>
              <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">How It Works</a>
              <a href="#pricing" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Pricing</a>
              <a href="/resources" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Resources</a>
              <Button className="bg-primary hover:bg-primary/90 text-white font-medium" onClick={() => window.location.href = '#contact'}>
                Get Started
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-20 pb-24 px-6 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://as1.ftcdn.net/jpg/03/92/25/22/1000_F_392252254_IGdYtYTHToZX7JKZuPm4jFc2ZfV0xUp5.webp" 
            alt="Mini data center in backyard" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/70"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-transparent to-white/80"></div>
        </div>
        
        {/* Floating green energy icons */}
        <div className="absolute top-1/4 right-10 z-10 opacity-20">
          <LucideIcons.Leaf className="w-16 h-16 text-green-600 animate-float-slow" />
        </div>
        <div className="absolute bottom-1/4 left-10 z-10 opacity-20">
          <LucideIcons.Zap className="w-12 h-12 text-primary animate-float-slow" style={{animationDelay: '1s'}} />
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center space-y-6 py-12">
            <Badge className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100 mb-4 px-4 py-1.5 font-medium">
              <LucideIcons.Leaf className="w-4 h-4 mr-2 inline" />
              100% Renewable Energy Powered
            </Badge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground leading-tight tracking-tight">
              AI in Every Home: Smart Data <br />Centers, Smarter Energy
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {mockData.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-semibold text-base px-8 shadow-lg">
                Explore the Platform
                <LucideIcons.ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-gray-300 text-foreground hover:bg-gray-50 font-semibold text-base px-8 bg-white/80 backdrop-blur-sm">
                See Demos
                <LucideIcons.Play className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos */}
      <section className="py-12 px-6 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8 items-center">
            {partners.map((partner, index) => (
              <div key={index} className="flex items-center justify-center">
                <div className="text-muted-foreground font-medium text-sm text-center opacity-60 hover:opacity-100 transition-opacity">
                  {partner}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Green Energy Impact Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100 mb-4 px-4 py-1.5 font-medium">
              <LucideIcons.Leaf className="w-4 h-4 mr-2 inline" />
              ENVIRONMENTAL IMPACT
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Powered by Nature, Built for the Future
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Each YardEdge unit operates on 100% renewable energy, creating a sustainable computing infrastructure
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LucideIcons.Sun className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">100%</div>
              <div className="text-sm text-muted-foreground">Solar Powered</div>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LucideIcons.Droplets className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">90%</div>
              <div className="text-sm text-muted-foreground">Water Savings</div>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LucideIcons.Trees className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">5 Tons</div>
              <div className="text-sm text-muted-foreground">CO₂ Offset Yearly</div>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LucideIcons.Zap className="w-6 h-6 text-primary" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">15kW</div>
              <div className="text-sm text-muted-foreground">Clean Energy Gen</div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Section */}
      <section id="platform" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="bg-primary/10 text-primary border-none hover:bg-primary/20 mb-4 px-4 py-1.5 font-medium">
              PLATFORM
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              The YardEdge Data Intelligence Platform
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Transform your unused outdoor space into a sustainable revenue stream
            </p>
          </div>
          
          {/* Technology Innovation Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Solar Powered Technology */}
            <div className="relative overflow-hidden rounded-xl shadow-lg group">
              <div className="relative h-72">
                <img 
                  src="https://images.pexels.com/photos/35425766/pexels-photo-35425766.jpeg?w=800&q=80" 
                  alt="Solar technology innovation" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center shadow-lg">
                    <LucideIcons.Sun className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl">Solar Powered Innovation</h3>
                    <p className="text-sm text-white/90">Advanced renewable energy systems</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-yellow-400 font-semibold text-sm">
                  <LucideIcons.TrendingUp className="w-5 h-5" />
                  <span>Zero-emission, 100% renewable energy operation</span>
                </div>
              </div>
            </div>

            {/* Eco-Friendly Design */}
            <div className="relative overflow-hidden rounded-xl shadow-lg group">
              <div className="relative h-72">
                <img 
                  src="https://images.unsplash.com/photo-1493612276216-ee3925520721?w=800&q=80" 
                  alt="Sustainable technology innovation" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center shadow-lg">
                    <LucideIcons.Lightbulb className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl">Eco-Friendly Technology</h3>
                    <p className="text-sm text-white/90">Cutting-edge sustainable design</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-green-400 font-semibold text-sm">
                  <LucideIcons.Leaf className="w-5 h-5" />
                  <span>Minimal environmental impact, maximum efficiency</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.benefits.slice(0, 3).map((benefit) => (
              <div key={benefit.id} className="text-center space-y-4">
                <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center mx-auto">
                  <div className="text-primary">
                    {renderIcon(benefit.icon)}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-foreground">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button variant="outline" className="border-gray-300 hover:bg-gray-50 font-medium">
              Explore the Platform
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Why Choose YardEdge</h2>
            <p className="text-lg text-muted-foreground">Complete infrastructure for your backyard</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.benefits.map((benefit) => (
              <Card key={benefit.id} className="bg-white border-gray-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <div className="text-primary">
                      {renderIcon(benefit.icon)}
                    </div>
                  </div>
                  <CardTitle className="text-foreground text-lg">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground">{benefit.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">How It Works</h2>
            <p className="text-lg text-muted-foreground">From concept to cash flow in four simple steps</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {mockData.howItWorks.map((step) => (
              <div key={step.id} className="relative">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-foreground">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Technology Overview - Simplified Public Version */}
          <div className="mt-20 border-t-2 border-gray-200 pt-20">
            <div className="text-center mb-12">
              <Badge className="bg-primary/10 text-primary border-none hover:bg-primary/20 mb-4 px-4 py-1.5 font-medium">
                TECHNOLOGY OVERVIEW
              </Badge>
              <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Meet the YardEdge Unit
              </h3>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                A compact, solar-powered mini data center designed for residential backyards
              </p>
            </div>

            {/* Key Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200">
                <CardContent className="pt-6">
                  <LucideIcons.Sun className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-foreground text-center mb-3">100% Solar Powered</h4>
                  <p className="text-muted-foreground text-center">
                    Completely self-sufficient renewable energy system with multi-day battery backup. Zero grid dependence.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
                <CardContent className="pt-6">
                  <LucideIcons.Cpu className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-foreground text-center mb-3">Enterprise Computing</h4>
                  <p className="text-muted-foreground text-center">
                    Professional-grade hardware delivering high-performance edge computing for AI workloads and data processing.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200">
                <CardContent className="pt-6">
                  <LucideIcons.Wind className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-foreground text-center mb-3">Intelligent Cooling</h4>
                  <p className="text-muted-foreground text-center">
                    Advanced thermal management system optimizes energy efficiency while maintaining optimal operating temperatures.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Performance Highlights */}
            <Card className="bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border-2 border-green-200 mb-12">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <LucideIcons.TrendingUp className="w-8 h-8 text-green-600" />
                  Energy Positive Operation
                </CardTitle>
                <CardDescription className="text-base">
                  Generates significantly more energy than consumed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-white rounded-lg">
                    <div className="text-4xl font-bold text-green-600 mb-2">Energy+</div>
                    <div className="text-sm text-muted-foreground">Net Positive Generation</div>
                  </div>
                  <div className="text-center p-6 bg-white rounded-lg">
                    <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
                    <div className="text-sm text-muted-foreground">Continuous Operation</div>
                  </div>
                  <div className="text-center p-6 bg-white rounded-lg">
                    <div className="text-4xl font-bold text-primary mb-2">99.5%</div>
                    <div className="text-sm text-muted-foreground">Uptime Guarantee</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action for Technical Details */}
            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
              <CardContent className="p-12 text-center">
                <LucideIcons.Lock className="w-16 h-16 mx-auto mb-6 text-primary" />
                <h4 className="text-3xl font-bold mb-4">Detailed Technical Specifications</h4>
                <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                  Complete engineering blueprints, component specifications, and installation details are available to verified investors and partners.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-primary hover:bg-primary/90 text-white font-semibold"
                    onClick={() => window.location.href = '/technical-specs'}
                  >
                    <LucideIcons.FileText className="w-5 h-5 mr-2" />
                    Access Technical Docs
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white text-white hover:bg-white hover:text-slate-900 font-semibold"
                    onClick={() => window.location.href = '#contact'}
                  >
                    <LucideIcons.Mail className="w-5 h-5 mr-2" />
                    Request Access
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Technical Specs Section */}
      <section id="tech-specs" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Technical Specifications</h2>
            <p className="text-lg text-muted-foreground">Enterprise-grade infrastructure</p>
          </div>
          <Tabs defaultValue="physical" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 bg-white border border-gray-200 mb-8 h-auto p-1">
              <TabsTrigger value="physical" className="data-[state=active]:bg-primary data-[state=active]:text-white py-3">
                <LucideIcons.Box className="w-4 h-4 mr-2" />
                Physical
              </TabsTrigger>
              <TabsTrigger value="power" className="data-[state=active]:bg-primary data-[state=active]:text-white py-3">
                <LucideIcons.Zap className="w-4 h-4 mr-2" />
                Power
              </TabsTrigger>
              <TabsTrigger value="computing" className="data-[state=active]:bg-primary data-[state=active]:text-white py-3">
                <LucideIcons.Cpu className="w-4 h-4 mr-2" />
                Computing
              </TabsTrigger>
              <TabsTrigger value="connectivity" className="data-[state=active]:bg-primary data-[state=active]:text-white py-3">
                <LucideIcons.Wifi className="w-4 h-4 mr-2" />
                Network
              </TabsTrigger>
            </TabsList>
            {Object.entries(mockData.techSpecs).map(([key, specs]) => (
              <TabsContent key={key} value={key}>
                <Card className="bg-white border-gray-200">
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {specs.map((spec, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <span className="text-muted-foreground font-medium">{spec.label}</span>
                          <span className="text-foreground font-semibold">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Investment Plans</h2>
            <p className="text-lg text-muted-foreground">Choose the configuration that fits your space and goals</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.pricingPlans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative ${
                  plan.popular 
                    ? 'border-primary border-2 shadow-lg' 
                    : 'border-gray-200'
                } bg-white`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-primary text-white border-none px-4 py-1 font-medium">
                      MOST POPULAR
                    </Badge>
                  </div>
                )}
                <CardHeader className="pt-8">
                  <CardTitle className="text-xl text-foreground mb-2">{plan.name}</CardTitle>
                  <div className="space-y-2">
                    <div className="text-4xl font-bold text-foreground">{plan.price}</div>
                    <div className="text-primary font-semibold">Earn {plan.revenue}</div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-muted-foreground">
                        <LucideIcons.Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full mt-6 ${
                      plan.popular
                        ? 'bg-primary hover:bg-primary/90 text-white'
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    } font-medium`}
                  >
                    Select {plan.name}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery & Video Section */}
      <section id="gallery" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="bg-primary/10 text-primary border-none hover:bg-primary/20 mb-4 px-4 py-1.5 font-medium">
              INSTALLATIONS
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">See It In Action</h2>
            <p className="text-lg text-muted-foreground">Watch modular data center installations and explore real deployments</p>
          </div>
          
          {/* Video */}
          <div className="mb-12">
            <Card className="bg-white border-gray-200 overflow-hidden">
              <CardContent className="p-0">
                <div className="aspect-video relative">
                  <iframe
                    width="100%"
                    height="100%"
                    src={mockData.video.embedUrl}
                    title={mockData.video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  ></iframe>
                </div>
                <div className="p-4 bg-gray-50">
                  <h3 className="font-semibold text-foreground">{mockData.video.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Timelapse of Dell modular data center build - showcasing the speed and efficiency of containerized deployment
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {mockData.gallery.map((image) => (
              <div 
                key={image.id} 
                className="aspect-square overflow-hidden rounded-lg border border-gray-200 hover:shadow-lg transition-shadow group cursor-pointer"
              >
                <img 
                  src={image.url} 
                  alt={image.alt} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-muted-foreground">Everything you need to know</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {mockData.faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`}
                className="bg-gray-50 border border-gray-200 rounded-lg px-6 data-[state=open]:border-primary transition-colors"
              >
                <AccordionTrigger className="text-foreground hover:text-primary text-left font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-2xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Get Started Today</h2>
            <p className="text-lg text-muted-foreground">Request a free site assessment</p>
          </div>
          <Card className="bg-white border-gray-200">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="text-foreground font-medium mb-2 block text-sm">Name *</label>
                  <Input 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    required
                    className="bg-white border-gray-300 text-foreground"
                  />
                </div>
                <div>
                  <label className="text-foreground font-medium mb-2 block text-sm">Email *</label>
                  <Input 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    required
                    className="bg-white border-gray-300 text-foreground"
                  />
                </div>
                <div>
                  <label className="text-foreground font-medium mb-2 block text-sm">Phone</label>
                  <Input 
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    className="bg-white border-gray-300 text-foreground"
                  />
                </div>
                <div>
                  <label className="text-foreground font-medium mb-2 block text-sm">Tell us about your property</label>
                  <Textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Property size, location, current energy setup..."
                    rows={4}
                    className="bg-white border-gray-300 text-foreground"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <LucideIcons.Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Request
                      <LucideIcons.Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 bg-foreground text-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
                  <LucideIcons.Zap className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">{mockData.company.name}</span>
              </div>
              <p className="text-gray-400 text-sm mb-3">{mockData.company.tagline}</p>
              <a href="https://theyardedge.com" className="text-primary hover:text-primary/80 transition-colors text-sm font-medium">
                TheYardEdge.com
              </a>
            </div>
            <div>
              <h3 className="font-bold mb-4">Product</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#platform" className="hover:text-primary transition-colors">Platform</a></li>
                <li><a href="#how-it-works" className="hover:text-primary transition-colors">How It Works</a></li>
                <li><a href="#pricing" className="hover:text-primary transition-colors">Pricing</a></li>
                <li><a href="#tech-specs" className="hover:text-primary transition-colors">Tech Specs</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Resources</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#faq" className="hover:text-primary transition-colors">FAQ</a></li>
                <li><a href="#gallery" className="hover:text-primary transition-colors">Gallery</a></li>
                <li><a href="#contact" className="hover:text-primary transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Support</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Connect</h3>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-primary rounded-lg flex items-center justify-center transition-colors">
                  <LucideIcons.Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-primary rounded-lg flex items-center justify-center transition-colors">
                  <LucideIcons.Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-primary rounded-lg flex items-center justify-center transition-colors">
                  <LucideIcons.Mail className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
            <p>&copy; 2025 {mockData.company.name}. All rights reserved. Building the distributed future. | <a href="https://theyardedge.com" className="text-primary hover:text-primary/80 transition-colors">TheYardEdge.com</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;